import { useEffect, useState, useRef, useCallback } from "react"
import { Avatar, Text } from "@/components/atoms"
import { Company } from "@/types/company"
import { getCompaniesV2 } from "@/services/companyService"
import { useAuth } from "@/contexts/AuthContext"
import { ListItem } from "../molecules"

interface CompanySelectionModalProps {
  onSelectCompany: (company: Company) => void
}

export const CompanySelectionModalContent = ({ onSelectCompany }: CompanySelectionModalProps) => {
  const [companies, setCompanies] = useState<Company[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const { user } = useAuth()

  // Ref para o elemento sentinela
  const sentinelRef = useRef<HTMLDivElement>(null)

  // 1. Função de busca principal
  const fetchCompanies = useCallback(
    async (isNewSearch: boolean) => {
      // Se não for busca nova e já estiver carregando ou não tiver mais, cancela
      if (!isNewSearch && (loading || !hasMore)) return

      setLoading(true)
      try {
        const targetPage = isNewSearch ? 1 : page
        const response = await getCompaniesV2({
          page: targetPage,
          limit: 10,
          search: searchTerm,
        })

        const newItems = response.data
        const metadata = response.meta

        setCompanies((prev) => (isNewSearch ? newItems : [...prev, ...newItems]))
        setHasMore(metadata.hasMore)

        // Se for busca nova, garante que o estado da página volte a 1
        if (isNewSearch) setPage(1)
      } catch (error) {
        console.error("Error fetching companies:", error)
      } finally {
        setLoading(false)
      }
    },
    [page, searchTerm, loading, hasMore]
  )

  // 2. Intersection Observer para o Sentinela
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1)
        }
      },
      { threshold: 0.1 } // Ativa quando 10% do sentinela aparecer
    )

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current)
    }

    return () => observer.disconnect()
  }, [hasMore, loading])

  // 3. Efeito disparado quando o observer incrementa a página
  useEffect(() => {
    if (page > 1) {
      fetchCompanies(false)
    }
  }, [page])

  // 4. Debounce para a barra de pesquisa
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCompanies(true)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchTerm, user.mentor_id])

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Input de Busca */}
      <div className="relative px-1">
        <input
          type="text"
          placeholder="Buscar empresa por nome..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-xl border border-zinc-700 bg-dark-500 px-4 py-2.5 text-sm text-white outline-none focus:ring-2 focus:ring-orange-500/50"
        />
      </div>

      {/* Lista */}
      <ul className="custom-scrollbar flex max-h-[400px] flex-col gap-2 overflow-y-auto pr-2">
        {companies.map((company, index) => (
          <ListItem
            key={`${company.empId}-${index}`}
            onClick={() => onSelectCompany(company)}
            className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-white/5"
          >
            <Avatar
              variant="primary"
              size="sm"
              alt={company.nomeFantasia}
              src={company.empLogoUrl}
            />
            <Text
              as="small"
              className="max-w-[400px] truncate font-medium"
            >
              {company.nomeFantasia}
            </Text>
          </ListItem>
        ))}

        {/* --- SENTINELA --- */}
        <div
          ref={sentinelRef}
          className="py-4 text-center"
        >
          {loading ? (
            <Text
              as="small"
              className="animate-pulse text-zinc-500"
            >
              Carregando mais...
            </Text>
          ) : !hasMore && companies.length > 0 ? (
            <Text
              as="small"
              className="text-zinc-600"
            >
              Fim da listagem
            </Text>
          ) : null}
        </div>

        {/* Empty State */}
        {!loading && companies.length === 0 && (
          <div className="py-8 text-center">
            <Text
              as="small"
              className="text-zinc-500"
            >
              Nenhuma empresa encontrada.
            </Text>
          </div>
        )}
      </ul>
    </div>
  )
}
